import { ChangeDetectorRef, Component, inject } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { NgGlyph } from "@ng-icons/core";
import { MaterialEffects } from "../../State/MaterialEffects";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { FileApi } from "../../../File/Api/FileApi";
import { CreateMaterialRequest } from "../../Dto/MaterialCreateRequest";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";

@Component({
  selector: "app-material-create",
  standalone: true,
  imports: [ReactiveFormsModule, AppButtonComponent, RichTextEditorComponent, NgGlyph],
  templateUrl: "./MaterialCreatePage.html",
  styleUrl: "./MaterialCreatePage.scss",
})
export class MaterialCreatePage {
  private readonly router = inject(Router);
  private readonly materialEffects = inject(MaterialEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly fileApi = inject(FileApi);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);

  materialForm: FormGroup;
  isSaving = false;

  priceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) {
        return { required: true };
      }
      const numValue = parseFloat(value);
      if (Number.isNaN(numValue) || numValue < 0) {
        return { min: true };
      }
      return null;
    };
  }

  optionalPositiveIntegerValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === undefined || value === "") {
        return null;
      }
      const parsed = Number(value);
      return (() => {
        if (Number.isInteger(parsed) && parsed > 0) {
          return null;
        }
        return { min: true };
      })();
    };
  }

  constructor() {
    this.materialForm = this.fb.group({
      name: ["", [Validators.required, Validators.minLength(2)]],
      sku: ["", [Validators.required, Validators.minLength(2)]],
      price: ["", [Validators.required, this.priceValidator()]],
      dcmSeriesId: ["", [this.optionalPositiveIntegerValidator()]],
      imageUrl: [""],
      description: [""],
    });
  }

  get avatarName(): string {
    return this.materialForm.get("name")?.value || "New material";
  }

  get avatarSku(): string {
    return this.materialForm.get("sku")?.value || "No SKU yet";
  }

  get avatarImageUrl(): string | null {
    const value = this.materialForm.get("imageUrl")?.value;
    return (() => {
      if (value) {
        return value;
      }
      return null;
    })();
  }

  async saveMaterial(): Promise<void> {
    if (this.materialForm.invalid) {
      setTimeout(() => {
        this.materialForm.markAllAsTouched();
      });
      this.toastEffects.show("Please fill in all required fields.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();

    const formData = this.materialForm.value;
    const imageUrl = (() => {
      if (typeof formData.imageUrl === "string") {
        return formData.imageUrl.trim();
      }
      return "";
    })();

    const payload: CreateMaterialRequest = {
      name: formData.name,
      sku: formData.sku,
      price: parseFloat(formData.price),
      dcmSeriesId: this.parseOptionalInteger(formData.dcmSeriesId),
    };

    if (imageUrl) {
      payload.imageUrl = imageUrl;
    }

    const description = (() => {
      if (typeof formData.description === "string") {
        return formData.description.trim();
      }
      return "";
    })();
    if (description) {
      payload.description = description;
    }

    try {
      await this.materialEffects.createMaterial(payload);
      this.toastEffects.show("Material created successfully.", "success");
      await this.router.navigate(["/materials"]);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to create material."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async onAvatarChange(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    try {
      const uploadedFile = await this.fileApi.uploadFile(file, { folder: "materials" });
      this.materialForm.patchValue({ imageUrl: uploadedFile.publicUrl });
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Failed to upload image"), "error");
    } finally {
      input.value = "";
    }
  }

  removeAvatar(): void {
    const currentImageUrl = this.materialForm.get("imageUrl")?.value;
    if (!currentImageUrl) {
      return;
    }

    this.confirmModalEffects.open({
      title: "Remove image?",
      message: "This will remove the uploaded image from the form.",
      confirmText: "Remove",
      cancelText: "Cancel",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: () => {
        this.materialForm.patchValue({ imageUrl: "" });
      },
    });
  }

  goBack(): void {
    this.router.navigate(["/materials"]).catch(() => undefined);
  }

  private parseOptionalInteger(value: unknown): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    return (() => {
      if (Number.isInteger(parsed) && parsed > 0) {
        return parsed;
      }
      return null;
    })();
  }
}
